import { useEffect, useState } from 'react'
import { withRouter } from '../utils'
import axios from 'axios'
import '../styles/login.css'
import { trackPageView } from '../tracker'

function Login({ navigate }) {
  const apiHost = process.env.REACT_APP_APIHOST || 'http://localhost:3033'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [inviteToken, setInviteToken] = useState(null)
  const [inviteUser, setInviteUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [settingPassword, setSettingPassword] = useState(false)
  const [phoneCode, setPhoneCode] = useState('+971')
  const [phoneNum, setPhoneNum] = useState('')
  const [loginError, setLoginError] = useState('')
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotSending, setForgotSending] = useState(false)
  const [resetToken, setResetToken] = useState(null)
  const [resetPw, setResetPw] = useState('')
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetting, setResetting] = useState(false)

  useEffect(() => {
    document.body.className = 'login-page'; trackPageView('/login')

    const params = new URLSearchParams(window.location.search)
    const reset = params.get('reset')
    if (reset) { setResetToken(reset); return () => { document.body.className = '' } }
    const token = params.get('token')
    if (token) {
      setInviteToken(token)
      axios.get(apiHost + '/invite/verify/' + token)
        .then(res => {
          if (res.data.status) setInviteUser(res.data.data)
          else { setLoginError('Invalid or expired invitation.'); setInviteToken(null) }
        })
        .catch(() => { setLoginError('Invalid or expired invitation.'); setInviteToken(null) })
    }

    return () => { document.body.className = '' }
  }, [apiHost])

  useEffect(() => {
    if (inviteToken) return
    let token = localStorage.getItem('token')
    let userRole = localStorage.getItem('user_role')
    if (token) {
      var _em = localStorage.getItem('user_email'); if (!_em) { try { _em = JSON.parse(atob(token.split('.')[1])).email } catch(e){} }
      if (_em === 'deck-viewer@forteintel.com') { localStorage.removeItem('token'); localStorage.removeItem('user_role'); localStorage.removeItem('user_email'); return }
      if (userRole === 'admin') navigate('/admin/dashboard')
      else navigate('/cockpit')
    }
  }, [navigate, inviteToken])

  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      const res = await axios.post(apiHost + '/heatmap/login', { email, password })
      localStorage.setItem('token', res.data.token)
      localStorage.setItem('user_role', res.data.role || '')
      localStorage.setItem('user_email', email)
      if (res.data.role === 'admin') navigate('/admin/dashboard')
      else if (res.data.email === 'deck-viewer@forteintel.com') window.location.href = '/deck'
      else navigate('/cockpit')
    } catch (err) {
      setLoginError(err?.response?.data?.errorMessage || 'Something went wrong.')
    }
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    setForgotSending(true)
    try {
      await axios.post(apiHost + '/invite/forgot-password', { email: forgotEmail })
      setForgotSent(true)
    } catch(err) { setLoginError('Failed to send reset link.') }
    setForgotSending(false)
  }

  const handleReset = async (e) => {
    e.preventDefault()
    if (resetPw.length < 6) { setLoginError('Password must be at least 6 characters.'); return }
    if (resetPw !== resetConfirm) { setLoginError('Passwords do not match.'); return }
    setResetting(true)
    try {
      await axios.post(apiHost + '/invite/reset-password', { token: resetToken, password: resetPw })
      // Auto-login: need to find user email first via verify
      try {
        const v = await axios.get(apiHost + '/invite/verify/' + resetToken)
        if (v.data.status) {
          const loginRes = await axios.post(apiHost + '/heatmap/login', { email: v.data.data.email, password: resetPw })
          localStorage.setItem('token', loginRes.data.token)
          localStorage.setItem('user_role', loginRes.data.role || '')
          localStorage.setItem('user_email', v.data.data.email)
          if (loginRes.data.email === 'deck-viewer@forteintel.com') window.location.href = '/deck'; else navigate('/cockpit')
          return
        }
      } catch(e2) {}
      // Token consumed, just redirect to login
      setResetToken(null)
      window.history.replaceState({}, '', '/login')
      setLoginError('')
    } catch(err) { setLoginError(err?.response?.data?.errorMessage || 'Failed to reset.') }
    setResetting(false)
  }

  const handleSetPassword = async (e) => {
    e.preventDefault()
    if (newPassword.length < 6) { setLoginError('Password must be at least 6 characters.'); return }
    if (newPassword !== confirmPassword) { setLoginError('Passwords do not match.'); return }
    setSettingPassword(true)
    try {
      const fullPhone = phoneNum ? (phoneCode + phoneNum.replace(/^0+/,'')) : ''
      const res = await axios.post(apiHost + '/invite/set-password', { token: inviteToken, password: newPassword, phoneNo: fullPhone })
      if (res.data.status) {
        // Auto-login and force intro popup
        const loginRes = await axios.post(apiHost + '/heatmap/login', { email: inviteUser.email, password: newPassword })
        localStorage.setItem('token', loginRes.data.token)
        localStorage.setItem('user_role', loginRes.data.role || '')
        localStorage.setItem('user_email', inviteUser.email)
        localStorage.removeItem('forte_intro_done')
        if (loginRes.data.role === 'admin') navigate('/admin/dashboard')
        else if (loginRes.data.email === 'deck-viewer@forteintel.com') window.location.href = '/deck'
        else navigate('/cockpit')
      }
    } catch (err) {
      setLoginError(err?.response?.data?.errorMessage || 'Something went wrong.')
    }
    setSettingPassword(false)
  }

  return (
    <>
      <header className="header-login center" id="header">
        <div className="auto__container">
          <div className="header__inner">
            <a href="/" className="header__inner-logo">
              <img src="icons/logo.webp" alt="logo" />
            </a>
          </div>
        </div>
      </header>

      <main className="main sm">
        <div className="main__bg">
          <img src="icons/bg-4.webp" alt="bg" />
        </div>

        <section className="login">
          <div className="auto__container">
            <div className="login__inner">
              {resetToken ? (
                <form className="loginForm" onSubmit={handleReset}>
                  <div className="loginForm__title">
                    <p>Reset your password</p>
                    <h1>New password</h1>
                  </div>
                  <div className="loginForm__col">
                    <div className="input">
                      <input type="password" placeholder="New password" value={resetPw} onChange={e => { setResetPw(e.target.value); setLoginError('') }} required minLength={6} />
                    </div>
                    <div className="input">
                      <input type="password" placeholder="Confirm password" value={resetConfirm} onChange={e => { setResetConfirm(e.target.value); setLoginError('') }} required minLength={6} />
                    </div>
                    {loginError && <div style={{fontSize:12,color:'#e55',background:'rgba(229,85,85,0.08)',border:'1px solid rgba(229,85,85,0.2)',borderRadius:3,padding:'10px 14px',marginBottom:8}}>{loginError}</div>}
                    <button className="button primary" type="submit" disabled={resetting}>{resetting ? 'RESETTING...' : 'RESET PASSWORD'}</button>
                  </div>
                </form>
              ) : forgotMode ? (
                <form className="loginForm" onSubmit={handleForgot}>
                  <div className="loginForm__title">
                    <p>Forgot your password?</p>
                    <h1>Reset password</h1>
                    <p style={{marginTop:'8px',fontSize:'13px',opacity:0.7}}>Enter your email and we'll send you a reset link.</p>
                  </div>
                  <div className="loginForm__col">
                    {forgotSent ? (
                      <div style={{fontSize:13,color:'#4CAF50',background:'rgba(76,175,80,0.08)',border:'1px solid rgba(76,175,80,0.2)',borderRadius:3,padding:'14px',textAlign:'center',lineHeight:1.6}}>
                        If an account exists for this email, a reset link has been sent. Check your inbox.
                      </div>
                    ) : (
                      <>
                        <div className="input">
                          <input type="email" placeholder="Email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required />
                        </div>
                        {loginError && <div style={{fontSize:12,color:'#e55',background:'rgba(229,85,85,0.08)',border:'1px solid rgba(229,85,85,0.2)',borderRadius:3,padding:'10px 14px',marginBottom:8}}>{loginError}</div>}
                        <button className="button primary" type="submit" disabled={forgotSending}>{forgotSending ? 'SENDING...' : 'SEND RESET LINK'}</button>
                      </>
                    )}
                    <div style={{textAlign:'center',marginTop:12}}>
                      <span onClick={() => { setForgotMode(false); setLoginError(''); setForgotSent(false) }} style={{fontSize:12,color:'#C29717',cursor:'pointer'}}>Back to sign in</span>
                    </div>
                  </div>
                </form>
              ) : inviteToken && inviteUser ? (
                <form className="loginForm" onSubmit={handleSetPassword}>
                  <div className="loginForm__title">
                    <p>Welcome to FORTE Intel</p>
                    <h1>Set your password</h1>
                    <p style={{marginTop:'8px',fontSize:'13px',opacity:0.7}}>{"Hi " + inviteUser.firstName + ", please choose a password to activate your account."}</p>
                  </div>
                  <div className="loginForm__col">
                    <div className="input">
                      <input type="email" value={inviteUser.email} disabled style={{opacity:0.5}} />
                    </div>
                    <div className="input" style={{display:'flex',gap:0}}>
                      <select value={phoneCode} onChange={e => setPhoneCode(e.target.value)} style={{width:90,background:'transparent',border:'none',borderRight:'1px solid rgba(255,255,255,0.1)',color:'#ccc',fontSize:13,outline:'none',cursor:'pointer',padding:'0 4px'}}>
                        {[
                          {c:'+971',f:'🇦🇪',n:'UAE'},{c:'+966',f:'🇸🇦',n:'KSA'},{c:'+974',f:'🇶🇦',n:'QAT'},{c:'+973',f:'🇧🇭',n:'BHR'},{c:'+965',f:'🇰🇼',n:'KWT'},{c:'+968',f:'🇴🇲',n:'OMN'},
                          {c:'+33',f:'🇫🇷',n:'FRA'},{c:'+44',f:'🇬🇧',n:'GBR'},{c:'+1',f:'🇺🇸',n:'USA'},{c:'+49',f:'🇩🇪',n:'DEU'},{c:'+41',f:'🇨🇭',n:'CHE'},{c:'+39',f:'🇮🇹',n:'ITA'},{c:'+34',f:'🇪🇸',n:'ESP'},
                          {c:'+351',f:'🇵🇹',n:'PRT'},{c:'+31',f:'🇳🇱',n:'NLD'},{c:'+32',f:'🇧🇪',n:'BEL'},{c:'+43',f:'🇦🇹',n:'AUT'},{c:'+46',f:'🇸🇪',n:'SWE'},{c:'+47',f:'🇳🇴',n:'NOR'},{c:'+45',f:'🇩🇰',n:'DNK'},{c:'+358',f:'🇫🇮',n:'FIN'},
                          {c:'+48',f:'🇵🇱',n:'POL'},{c:'+420',f:'🇨🇿',n:'CZE'},{c:'+36',f:'🇭🇺',n:'HUN'},{c:'+40',f:'🇷🇴',n:'ROU'},{c:'+30',f:'🇬🇷',n:'GRC'},{c:'+353',f:'🇮🇪',n:'IRL'},
                          {c:'+90',f:'🇹🇷',n:'TUR'},{c:'+20',f:'🇪🇬',n:'EGY'},{c:'+962',f:'🇯🇴',n:'JOR'},{c:'+961',f:'🇱🇧',n:'LBN'},{c:'+964',f:'🇮🇶',n:'IRQ'},{c:'+98',f:'🇮🇷',n:'IRN'},
                          {c:'+972',f:'🇮🇱',n:'ISR'},{c:'+970',f:'🇵🇸',n:'PSE'},{c:'+963',f:'🇸🇾',n:'SYR'},{c:'+967',f:'🇾🇪',n:'YEM'},{c:'+218',f:'🇱🇾',n:'LBY'},{c:'+216',f:'🇹🇳',n:'TUN'},{c:'+213',f:'🇩🇿',n:'DZA'},{c:'+212',f:'🇲🇦',n:'MAR'},
                          {c:'+249',f:'🇸🇩',n:'SDN'},{c:'+252',f:'🇸🇴',n:'SOM'},{c:'+251',f:'🇪🇹',n:'ETH'},{c:'+234',f:'🇳🇬',n:'NGA'},{c:'+254',f:'🇰🇪',n:'KEN'},{c:'+27',f:'🇿🇦',n:'ZAF'},{c:'+233',f:'🇬🇭',n:'GHA'},{c:'+255',f:'🇹🇿',n:'TZA'},
                          {c:'+91',f:'🇮🇳',n:'IND'},{c:'+92',f:'🇵🇰',n:'PAK'},{c:'+93',f:'🇦🇫',n:'AFG'},{c:'+94',f:'🇱🇰',n:'LKA'},{c:'+880',f:'🇧🇩',n:'BGD'},
                          {c:'+86',f:'🇨🇳',n:'CHN'},{c:'+81',f:'🇯🇵',n:'JPN'},{c:'+82',f:'🇰🇷',n:'KOR'},{c:'+852',f:'🇭🇰',n:'HKG'},{c:'+65',f:'🇸🇬',n:'SGP'},{c:'+60',f:'🇲🇾',n:'MYS'},{c:'+66',f:'🇹🇭',n:'THA'},{c:'+62',f:'🇮🇩',n:'IDN'},{c:'+63',f:'🇵🇭',n:'PHL'},{c:'+84',f:'🇻🇳',n:'VNM'},
                          {c:'+55',f:'🇧🇷',n:'BRA'},{c:'+52',f:'🇲🇽',n:'MEX'},{c:'+57',f:'🇨🇴',n:'COL'},{c:'+56',f:'🇨🇱',n:'CHL'},{c:'+54',f:'🇦🇷',n:'ARG'},{c:'+51',f:'🇵🇪',n:'PER'},
                          {c:'+61',f:'🇦🇺',n:'AUS'},{c:'+64',f:'🇳🇿',n:'NZL'},{c:'+7',f:'🇷🇺',n:'RUS'},{c:'+380',f:'🇺🇦',n:'UKR'},
                        ].map(x => <option key={x.c} value={x.c}>{x.f} {x.c}</option>)}
                      </select>
                      <input type="tel" placeholder="Phone number" value={phoneNum} onChange={e => setPhoneNum(e.target.value)} style={{flex:1}} />
                    </div>
                    <div className="input">
                      <input type="password" placeholder="Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} />
                    </div>
                    <div className="input">
                      <input type="password" placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} />
                    </div>
                    {loginError && <div style={{fontSize:12,color:'#e55',background:'rgba(229,85,85,0.08)',border:'1px solid rgba(229,85,85,0.2)',borderRadius:3,padding:'10px 14px',marginBottom:8}}>{loginError}</div>}
                    <button className="button primary" type="submit" disabled={settingPassword}>
                      {settingPassword ? 'SETTING UP...' : 'ACTIVATE ACCOUNT'}
                    </button>
                  </div>
                </form>
              ) : (
                <form className="loginForm" onSubmit={handleLogin}>
                  <div className="loginForm__title">
                    <p>Navigating risk with strategic foresight.</p>
                    <h1>Sign-in</h1>
                  </div>
                  <div className="loginForm__col">
                    <div className="input">
                      <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
                    </div>
                    <div className="input">
                      <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
                    </div>
                    {loginError && <div style={{fontSize:12,color:'#e55',background:'rgba(229,85,85,0.08)',border:'1px solid rgba(229,85,85,0.2)',borderRadius:3,padding:'10px 14px',marginBottom:8}}>{loginError}</div>}
                    <button className="button primary" type="submit">LOGIN</button>
                    <div style={{textAlign:'center',marginTop:12,display:'flex',flexDirection:'column',gap:8}}>
                      <span onClick={() => { setForgotMode(true); setLoginError('') }} style={{fontSize:12,color:'#888',cursor:'pointer'}}>Lost my password</span>
                      <a href="/daily-reports" style={{fontSize:12,color:'#C29717',textDecoration:'none'}}>Request an access</a>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        <footer className="footer sm">
          <div className="auto__container">
            <div className="footer__inner">
              <div className="footer__inner-copy">
                <div className="footer__inner-side">
                  <div className="footer__inner-logo">
                    <img src="icons/logo.webp" alt="logo" />
                  </div>
                  <p>FORTE INTEL | 2026</p>
                </div>
                <div className="footer__inner-links">
                  <a href="#mission">MISSION</a>
                  <a href="#services">SERVICES</a>
                  <a href="#coverage">COVERAGE</a>
                  <a href="#partners">PARTNERS</a>
                  <a href="#contact">CONTACTS</a>
                  <a href="/privacy" className="footer__inner-log">PRIVACY</a>
                </div>
                <a href="/login" className="footer__inner-log">LOGIN</a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </>
  )
}

export default withRouter(Login)
